import { computed, nextTick, toValue } from 'vue'
import type { Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'

type MaybeRefOrGetter<T> = T | Ref<T> | (() => T)

export interface CtaOptions {
  id?: string
  type: 'internal' | 'external' | 'email' | 'phone' | 'scroll'
  to?: string
  text: string
  subtitle?: string
  icon?: string
  iconPosition?: 'left' | 'right'
  appearance?: 'button' | 'link'
  color?: 'primary' | 'secondary' | 'accent' | 'neutral'
  targetEl?: MaybeRefOrGetter<HTMLElement | null | string>
}

export function useCta(options: MaybeRefOrGetter<CtaOptions>) {
  const route = useRoute()
  const router = useRouter()
  const opt = computed(() => toValue(options))

  const isInternal = computed(() => opt.value.type === 'internal')
  const isExternal = computed(() => opt.value.type === 'external')
  const isEmail = computed(() => opt.value.type === 'email')
  const isPhone = computed(() => opt.value.type === 'phone')
  const isScroll = computed(() => opt.value.type === 'scroll')

  const href = computed(() =>
    isExternal.value ? opt.value.to :
    isEmail.value ? `mailto:${opt.value.to}` :
    isPhone.value ? `tel:${opt.value.to}` :
    undefined
  )

  const routerTo = computed<RouteLocationRaw | undefined>(() =>
    isInternal.value ? opt.value.to : undefined
  )

  const target = computed(() => (isExternal.value ? '_blank' : undefined))
  const rel = computed(() => (isExternal.value ? 'noopener' : undefined))
  const appearance = computed(() => opt.value.appearance ?? 'button')
  const iconPosition = computed(() => opt.value.iconPosition ?? 'right')

  const colorClass = computed(() =>
    appearance.value === 'link'
      ? opt.value.color === 'secondary'
        ? 'link-ui--secondary'
        : opt.value.color === 'accent'
        ? 'link-ui--accent'
        : opt.value.color === 'neutral'
        ? 'link-ui--neutral'
        : 'link-ui--primary'
      : opt.value.color === 'secondary'
      ? 'btn-ui--secondary'
      : opt.value.color === 'accent'
      ? 'btn-ui--secondary'
      : opt.value.color === 'neutral'
      ? 'btn-ui--muted'
      : 'btn-ui--primary'
  )

  function getScrollElement() {
    const targetEl = toValue(opt.value.targetEl)

    if (typeof targetEl === 'string') {
      return document.querySelector<HTMLElement>(targetEl)
    }

    return targetEl ?? null
  }

  function waitForScrollElement(timeoutMs = 1500) {
    const existing = getScrollElement()
    if (existing) return Promise.resolve(existing)

    if (typeof window === 'undefined') return Promise.resolve(null)

    const startedAt = window.performance.now()

    return new Promise<HTMLElement | null>((resolve) => {
      function check() {
        const el = getScrollElement()
        if (el) {
          resolve(el)
          return
        }

        if (window.performance.now() - startedAt >= timeoutMs) {
          resolve(null)
          return
        }

        window.requestAnimationFrame(check)
      }

      window.requestAnimationFrame(check)
    })
  }

  async function onClick(e: Event) {
    if (!isScroll.value) return
    e.preventDefault()

    if (opt.value.to) {
      const resolved = router.resolve(opt.value.to)
      if (resolved.fullPath !== route.fullPath) {
        await router.push(opt.value.to)
      }

      await nextTick()
    }

    const el = await waitForScrollElement()
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return {
    isInternal,
    isExternal,
    isEmail,
    isPhone,
    isScroll,
    href,
    routerTo,
    target,
    rel,
    appearance,
    iconPosition,
    colorClass,
    text: computed(() => opt.value.text),
    icon: computed(() => opt.value.icon).value,
    onClick,
  }
}
